import Vue from 'vue';
import Router, { RawLocation, Route } from 'vue-router';
import { ActionTree, Dictionary, MutationTree } from 'vuex';

import { CollectionType } from '@/enums/collection-type';

import { getAxios } from '@/helpers/axios-helper';

import { tabTypeMigrationMap } from '@/interfaces/inner-tabs';
import { UrlMetadataResponse } from '@/interfaces/responses/url-metadata';
import { Clip } from '@/interfaces/titles';

import { RouterMeta } from '@/routing/config';

const { isNavigationFailure, NavigationFailureType } = Router;

export type RoutingState = {
	activeRoute: Route | null;
	lastRoute: Route | null;
	urlMetadata: UrlMetadataResponse | null;
	lastUrlMetadata: UrlMetadataResponse | null;
	// TODO We're assuming implicitly this would only be called after promise is registered. That should not be the case.
	// An event that other modules can register to - so they get the data when it's there.
	urlMetadataPromise: Promise<UrlMetadataResponse> | null;
	// this is used to fix the lazy loading of components with ion tabs.
	// [tab]: true when activated once
	activatedTabs: Record<string, boolean>;
	lastActiveCollectionType: CollectionType | null;
	scrollPositions: Partial<Record<RouterMeta['tab'], number>>;
};

type RoutingGetter<T> = (state: RoutingState, getters: any) => T;

export type RoutingGetters = {
	queryParams: RoutingGetter<Route['query']>;
	urlMetadata: RoutingGetter<RoutingState['urlMetadata']>;
	urlMetaDataReleaseDate: RoutingGetter<Date | undefined>;
	urlMetaDataClips: RoutingGetter<Clip[]>;
	activeCollectionType: RoutingGetter<CollectionType>;
	lastActiveCollectionType: RoutingGetter<CollectionType | null>;
	isSeasonPage: RoutingGetter<boolean>;
	isShowPage: RoutingGetter<boolean>;
};

export type RoutingStore = {
	state: RoutingState;
	getters: RoutingGetters;
};

const state = () =>
	({
		activeRoute: null,
		lastRoute: null,
		urlMetadata: null,
		lastUrlMetadata: null,
		urlMetadataPromise: new Promise((resolve, reject) => {}),
		activatedTabs: {},
		lastActiveCollectionType: null,
		scrollPositions: {},
	} as RoutingState);

const getters: RoutingGetters = {
	queryParams(state): Route['query'] {
		if (state.activeRoute) {
			// TODO Unsure if casting makes sense here.
			return state.activeRoute.query as Dictionary<string | string[]>;
		} else {
			return {};
		}
	},
	urlMetadata(state): RoutingState['urlMetadata'] {
		const urlMetadata = (state.urlMetadata || {}) as UrlMetadataResponse;
		const heading_1 = urlMetadata.heading_1;
		return {
			...urlMetadata,
			...{ heading_1 },
		};
	},
	urlMetaDataReleaseDate(state, getters): Date | undefined {
		const urlMetadata = getters.urlMetadata;
		if (!urlMetadata.html_content || !urlMetadata.html_content.release_date) {
			return;
		}
		return new Date(urlMetadata.html_conent.release_date);
	},
	urlMetaDataClips(state, getters): Clip[] {
		const urlMetadata = getters.urlMetadata;
		if (!urlMetadata || !urlMetadata.html_content) {
			return [];
		}
		let trailers = urlMetadata.html_content.trailers || ((urlMetadata.parent || {}).html_content || {}).trailers;
		// trailers may be returned as strings from the API
		if (typeof trailers === 'string') {
			trailers = JSON.parse(trailers);
		}
		if (trailers) {
			return trailers;
		}
		return [];
	},
	activeCollectionType(state): CollectionType {
		return state.activeRoute ? (state.activeRoute.meta || {}).collectionType : null;
	},
	lastActiveCollectionType(state, getters): CollectionType | null {
		const currentCollectionType = getters.activeCollectionType;
		if (currentCollectionType) {
			return currentCollectionType;
		}

		const { lastActiveCollectionType } = state;
		if (lastActiveCollectionType) {
			return lastActiveCollectionType;
		}

		// This is a failsafe, it should never land here. Gotta investigate or rewrite this
		return CollectionType.GLOBAL;
	},

	isSeasonPage(state) {
		return (state.activeRoute?.name || '').includes('app.detail.show.season');
	},

	isShowPage(state) {
		return (state.activeRoute?.name || '').includes('app.detail.show');
	},
};

const actions: ActionTree<RoutingState, any> = {
	async fetchUrlMetadata({ commit, state }, url: string) {
		let path = url.split('?')[0];
		if (path !== '/' && !path.includes('shared')) {
			path = path.replace(/\/$/, ''); // remove trailing backslash
			try {
				const promise = getAxios()
					.get(`${JW_CONFIG.API}/urls`, {
						params: {
							path,
						},
					})
					.then(({ data }) => data);
				commit('SET_URL_METADATA_PROMISE', promise);
				const urlMetadata = await promise;
				commit('SET_LAST_URL_METADATA', state.urlMetadata);
				commit('SET_URL_METADATA', urlMetadata);
			} catch (err) {
				console.error('[RoutingStore] fetchUrlMetadata', err);
				commit('SET_URL_METADATA_PROMISE', Promise.resolve());
			}

			// @todo netflix 4k exception
			// @todo maybe set customCanonical to meta.store.ts state?
		} else {
			const homeMockMeta = {
				meta_title: 'JustWatch - The Streaming Guide',
				meta_description: 'All your streaming services in one app.',
				meta_keywords: '',
			};
			commit('SET_LAST_URL_METADATA', state.urlMetadata);
			commit('SET_URL_METADATA', homeMockMeta);
			commit('SET_URL_METADATA_PROMISE', Promise.resolve());
		}
	},

	async setActiveRoute({ commit }, { activeRoute, router }: { activeRoute: Route; router: Router }) {
		const meta = { ...activeRoute.meta };

		// Migration from old TabType for NewWatchlist
		const { query } = activeRoute;
		const innerTab: string = (query.inner_tab as string) || '';
		if (innerTab) {
			if (Object.keys(tabTypeMigrationMap).includes(innerTab)) {
				query.inner_tab = tabTypeMigrationMap[innerTab as keyof typeof tabTypeMigrationMap];

				router.replace(activeRoute as RawLocation).catch((error: any) => {
					if (
						!isNavigationFailure(error, NavigationFailureType.duplicated) &&
						!isNavigationFailure(error, NavigationFailureType.cancelled)
						// Note(valerio.mazza) Search.vue component generates a navigation canceled due to
						// a reset of the filters which sometimes triggers a navigation
					) {
						throw error;
					}
				});

				return;
			}
		}

		// exclude 'matched' that causes circular dependency
		/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
		const { matched, ...route } = activeRoute;
		commit('SET_ACTIVE_ROUTE', { ...route, path: decodeURI(route.path), meta });
		commit('ACTIVATE_TAB', activeRoute?.meta?.tab);
	},
	// async setActiveRoute({ commit, rootGetters }, activeRoute: Route) {
	// 	const meta = { ...activeRoute.meta };

	// // Migration from old TabType for NewWatchlist
	// const { query } = activeRoute;
	// const innerTab: string = (query.inner_tab as string) || '';
	// if (innerTab) {
	// 	if (Object.keys(tabTypeMigrationMap).includes(innerTab)) {
	// 		query.inner_tab = tabTypeMigrationMap[innerTab as keyof typeof tabTypeMigrationMap];

	// 		getStateRouter().replace(activeRoute as RawLocation);
	// 		return;
	// 	}
	// }

	// 	commit('SET_ACTIVE_ROUTE', { ...activeRoute, meta });
	// 	commit('ACTIVATE_TAB', activeRoute.meta.tab);

	// 	// exclude paths from looking up, e.g. "/" - exclude mobile only 'pages'
	// if (['/'].indexOf(activeRoute.path) === -1 && !activeRoute.path.includes('leaving-soon')) {
	// 		let isNetflix4kException = false;
	// 		const promise = axios
	// 			.get(JW_CONFIG.API + '/urls', {
	// 				params: {
	// 					include_children: true,
	// 					path: activeRoute.path,
	// 				},
	// 			})
	// 			.then(result => {
	// 				isNetflix4kException =
	// 					activeRoute.name === 'app.titles.popular.provider' &&
	// 					JSON.stringify(activeRoute.query) === '{"presentation_types":"4k"}' &&
	// 					JSON.stringify(activeRoute.params) === '{"provider":"netflix"}' &&
	// 					rootGetters['language/language'] === 'en';
	// 				// @todo remove ugly netflix 4k hack
	// 				if (isNetflix4kException) {
	// 					const netflix4kContentData = {
	// 						meta_title: 'Netflix 4K - Full List of all the Netflix 4K content',
	// 						meta_description:
	// 							'Find a complete and daily updated list of the 4K content available on Netflix. JustWatch lists and sorts by popularity all the Netflix 4K movies and 4K shows, so you can easily enjoy the 4K streaming on Netflix.',
	// 						meta_keywords:
	// 							'Netflix 4K, Netflix 4K content, Netflix 4K movies, 4K shows on netflix, Netflix 4K streaming',
	// 						// heading_1: 'Netflix 4K',
	// 						html_content: {
	// 							filter_content: [
	// 								'<h1>Netflix 4K</h1>',
	// 								'<p>What can you watch in 4K on Netflix? You are lost in the ever growing catalogue of Netflix shows in 4k? You can\'t find the best 4K movies on Netflix? Search no more! JustWatch created the ultimate list of movies and TV shows you can stream in 4K on Netflix. Browse now through all the Netflix 4K content. Or, if you prefer Netflix movies, select the filter "Movies" and enjoy our Netflix 4K movies list. Sorted by popularity, you can easily choose the best 4K movies on Netflix right now. For TV shows fans, we have the TV show filter. Select "TV Shows" in the filter bar below and get all the 4K shows on Netflix.</p>',
	// 								'<h1>Netflix 4K plan</h1>',
	// 								"<p>Netflix 4K streaming is not available on Netflix for all its subscribers. Only the subscribers of the premium tier can enjoy Netflix; the premium tier is the most expensive Netflix plan and allows, on top of 4K streaming, the possibility to watch movies and tv shows on up to four screens. The flat rate is always changeable for a cheaper one (which won't have 4K streaming though) and you can quit the subscription at any time. The 4K streaming video quality on Netflix starts at 2160p.</p>",
	// 							].join(''),
	// 						},
	// 					};
	// 					result.data.meta_title = netflix4kContentData.meta_title;
	// 					result.data.meta_description = netflix4kContentData.meta_description;
	// 					result.data.meta_keywords = netflix4kContentData.meta_keywords;
	// 					result.data.html_content.filter_content = netflix4kContentData.html_content.filter_content;
	// 					result.data.href_lang_tags = [];
	// 				}
	// 				return result.data;
	// 			});

	// 		commit('SET_URL_METADATA_PROMISE', promise);

	// 		//@todo: cache metadata
	// 		const urlMetadata = await promise;
	// 		commit('SET_URL_METADATA', urlMetadata);

	// 		const customCanonical = isNetflix4kException ? activeRoute.fullPath : undefined;
	// 		MetaHelper.setProperties(urlMetadata, activeRoute.fullPath, customCanonical);
	// 		JsonLDHelper.resetJsonLd();
	// 	} else {
	// 		commit('SET_URL_METADATA_PROMISE', Promise.resolve());
	// 		commit('SET_URL_METADATA', {});
	// 	}
	// },
};

const mutations: MutationTree<RoutingState> = {
	SET_ACTIVE_ROUTE(state, activeRoute) {
		Vue.set(state, 'lastRoute', { ...state.activeRoute });
		Vue.set(state, 'activeRoute', { ...activeRoute });
		// only set lastActiveCollectionType of the last route that had a collectionType
		if (activeRoute && (activeRoute.meta || {}).collectionType) {
			Vue.set(state, 'lastActiveCollectionType', activeRoute.meta.collectionType);
		}
	},
	SET_URL_METADATA(state, urlMetadata) {
		state.urlMetadata = urlMetadata;
	},
	SET_LAST_URL_METADATA(state, lastUrlMetadata) {
		state.lastUrlMetadata = lastUrlMetadata;
	},
	SET_URL_METADATA_PROMISE(state, urlMetadataPromise) {
		state.urlMetadataPromise = urlMetadataPromise;
	},
	ACTIVATE_TAB(state, tab: string) {
		if (tab) {
			state.activatedTabs = {
				...state.activatedTabs,
				[tab]: true,
			};
		}
	},
	SET_SCROLL_POSITION(state, { tab, y }: { tab: RouterMeta['tab']; y: number }) {
		if (tab) Vue.set(state, 'scrollPositions', { ...state.scrollPositions, [tab]: y });
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
